import React, { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { Button } from "./button";
import { Input } from "./input";
import { Label } from "./label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./dialog";
import { useAuth } from "../../contexts/AuthContext";

// Step-up re-authentication before a safety-critical action (TP-SAF-WEB-TS-07-TC-0003
// FT-004). Opened when the service answers REAUTH_REQUIRED; onConfirmed runs only
// after the credentials were re-entered successfully, and the caller retries then.
export function ReauthDialog({ open, onOpenChange, onConfirmed, actionLabel = "continue" }) {
  const { user, reauthenticate, confirmReauthentication, logout } = useAuth();
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [needsCode, setNeedsCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [signInRequired, setSignInRequired] = useState(false);

  // Every opening starts clean; nothing typed survives a close.
  useEffect(() => {
    if (!open) return;
    setPassword("");
    setCode("");
    setNeedsCode(false);
    setError(null);
    setSignInRequired(false);
  }, [open]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = needsCode ? await confirmReauthentication(code.trim()) : await reauthenticate(password);
      if (result?.codeRequired) {
        setNeedsCode(true);
        setPassword("");
        return;
      }
      onOpenChange(false);
      await onConfirmed?.();
    } catch (err) {
      if (err?.signInRequired) setSignInRequired(true);
      setError(err?.message || "Re-authentication failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!busy) onOpenChange(next); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-blue-700" />
            Confirm your identity
          </DialogTitle>
          <DialogDescription>
            {needsCode
              ? "Enter the code from your authenticator app."
              : `For your security, re-enter your password to ${actionLabel}.`}
          </DialogDescription>
        </DialogHeader>

        {signInRequired ? (
          <>
            <p className="text-sm text-slate-600">{error}</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="button" onClick={() => logout("user")}>Sign in again</Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {needsCode ? (
              <div className="space-y-1.5">
                <Label htmlFor="reauth-code">Authentication code</Label>
                <Input
                  id="reauth-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="reauth-email">Account</Label>
                <Input id="reauth-email" value={user?.email || ""} readOnly disabled />
                <Label htmlFor="reauth-password">Password</Label>
                <Input
                  id="reauth-password"
                  type="password"
                  autoComplete="current-password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            )}
            {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={busy || (needsCode ? code.trim().length !== 6 : !password)}>
                {busy ? "Verifying…" : "Confirm"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
